import { beginMutation } from '@/lib/prefetch-invalidation';

// Response-level boundary for callers with their own error/body contracts.
// Ordinary JSON callers use apiRequest below; presence/telemetry stay outside.
export async function apiFetch(url, options = {}) {
  const mutating = (options.method || 'GET').toUpperCase() !== 'GET';
  const finish = mutating ? beginMutation() : null;
  try {
    return await fetch(url, options);
  } finally {
    finish?.();
  }
}

export function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

export function isNotFoundError(error) {
  return error?.status === 404;
}

// Build the Error thrown for a non-ok response, carrying the server's structured
// fields so callers can react to them. `errorCode`/`redirectTo` power the
// Plus-only AI gate: RequireAiAccess answers an XHR from a free user with
// `errorCode: 'ai_upgrade_required'` + `redirectTo: <tour>`, and a caller (e.g.
// the journal mic) relocates there instead of showing a generic error toast.
// Some endpoints spell it `error_code` (the chat daily cap), so both arrive as
// `errorCode`. `status` is the HTTP status, for callers that tell a 422 from a
// 429 (chat phrase dictation, plan 0274). `messageFromServer` marks a message
// the server wrote, in the user's language: a proxy's HTML error page leaves
// the English fallback, which a caller should not show as it is.
async function requestError(res) {
  const data = (await res.json().catch(() => null)) || {};
  const err = new Error(data.error || 'Request failed');
  err.status = res.status;
  if (typeof data.error === 'string' && data.error) {
    err.messageFromServer = true;
  }
  const errorCode = data.errorCode || data.error_code;
  if (errorCode) err.errorCode = errorCode;
  if (data.redirectTo) err.redirectTo = data.redirectTo;
  return err;
}

// The session is gone: leave for the login page, and still reject so the
// caller stops what it was doing.
function sessionExpired() {
  window.location.href = '/login';
  const err = new Error('Session expired');
  err.status = 401;
  return err;
}

export async function apiRequest(
  url,
  method = 'GET',
  body = null,
  options = {},
) {
  const { signal, timeoutMs } = options;
  if (!timeoutMs) return requestJson(url, method, body, options);
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(abort, timeoutMs);
  try {
    return await requestJson(url, method, body, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

async function requestJson(url, method, body, { signal, keepalive } = {}) {
  method = method.toUpperCase();
  const headers = {
    Accept: 'application/json',
    'X-CSRF-Token': csrfToken(),
  };

  const options = { method, headers, signal };
  if (keepalive !== undefined) options.keepalive = keepalive;

  if (body && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const res = await apiFetch(url, options);

  if (res.status === 401) throw sessionExpired();

  if (!res.ok) {
    throw await requestError(res);
  }

  const text = await res.text();
  return text.trim() ? JSON.parse(text) : null;
}

export async function apiFormRequest(url, formData, { signal } = {}) {
  const res = await apiFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: formData,
    signal,
  });

  if (res.status === 401) throw sessionExpired();

  if (!res.ok) {
    throw await requestError(res);
  }

  const text = await res.text();
  return text.trim() ? JSON.parse(text) : null;
}
